import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'
import cx from 'helpers/cx'
import getCleanUrl from 'helpers/getCleanUrl'
import { textContrastIsAA } from 'helpers/getContrast'
import isExternalUrl from 'helpers/isExternalUrl'

import { ChevronDownIcon } from '@pleio/icons/chevron-down'
import { NewWindowSmallIcon } from '@pleio/icons/new-window-small'

import DropdownButton from 'js/components/DropdownButton'

import styles from './NavigationMenuItem.module.css'

/**
 * Build the class list for a menu item.
 *
 * @param interactive - whether hover/active/aria-current styling applies
 *   (originally driven by `!$showIcon`).
 * @param currentReadable - whether header text passes AA contrast on white,
 *   selecting the coloured-text/white-pill variant of the aria-current state.
 */
export const getMenuItemClassName = (
    interactive: boolean,
    currentReadable: boolean,
) =>
    cx(
        styles.menuItem,
        interactive && styles.interactive,
        interactive && currentReadable && styles.currentReadable,
    )

const NavigationMenuItem = ({ item }) => {
    const { t } = useTranslation()

    const headerMain = getComputedStyle(document.documentElement)
        .getPropertyValue('--ref-palette-header-main')
        .trim()
    const currentReadable = textContrastIsAA(headerMain)
    const menuItemClassName = getMenuItemClassName(true, currentReadable)

    const hasSubItems = item.children.length > 0
    const itemLink = getCleanUrl(item.link)
    const itemExternal = isExternalUrl(itemLink)
    const label = item.localLabel || item.label || itemLink

    if (hasSubItems) {
        const itemOptions = item.children.map((subItem) => {
            const subItemOptions = subItem.children.map((subSubitem) => {
                return {
                    url: subSubitem.link,
                    label:
                        subSubitem.localLabel ||
                        subSubitem.label ||
                        getCleanUrl(subSubitem.link),
                    navLink: true,
                }
            })

            return {
                url: subItem.link,
                label:
                    subItem.localLabel ||
                    subItem.label ||
                    getCleanUrl(subItem.link),
                subOptions: subItemOptions,
                navLink: true,
            }
        })

        return (
            <li>
                <DropdownButton
                    options={itemOptions}
                    trigger="hover"
                    side="bottom"
                    align="start"
                    arrow={true}
                    showArrow={true}
                    arrowElement={
                        <ChevronDownIcon style={{ marginLeft: '8px' }} />
                    }
                >
                    <button type="button" className={menuItemClassName}>
                        <span>{label}</span>
                    </button>
                </DropdownButton>
            </li>
        )
    } else {
        return (
            <li>
                {itemExternal ? (
                    <a
                        className={menuItemClassName}
                        href={itemLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${label}${t(
                            'global.opens-in-new-window',
                        )}`}
                    >
                        <span>{label}</span>
                        <NewWindowSmallIcon
                            style={{
                                marginLeft: '8px',
                                marginBottom: '2px',
                            }}
                        />
                    </a>
                ) : (
                    <NavLink className={menuItemClassName} to={itemLink}>
                        <span>{label}</span>
                    </NavLink>
                )}
            </li>
        )
    }
}

export default NavigationMenuItem
